import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { InlineWidget, useCalendlyEventListener } from 'react-calendly';
import SectionTitle from './SectionTitle';
import { useTheme } from '../context/ThemeContext';
import { trackContactClick } from '../utils/analytics';

const CALENDLY_URL = 'https://calendly.com/d/dtbv-3sr-wjm/meet-with-cnd';
const CALENDLY_ORIGINS = [
  'https://calendly.com',
  'https://assets.calendly.com',
];

const ContactSection = styled.section`
  padding: 120px 56px;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.primary};
  position: relative;

  @media (max-width: 768px) {
    padding: 80px 20px;
  }
`;

const ContentWrapper = styled.div`
  max-width: 880px;
  margin: 0 auto;
`;

const BookingCard = styled(motion.div)`
  background: ${({ theme }) => theme.colors.bg};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 12px;
  padding: 40px 32px 24px;

  @media (max-width: 768px) {
    padding: 28px 16px 16px;
  }
`;

const BookingHeader = styled.div`
  text-align: center;
  margin-bottom: 24px;
`;

const BookingEye = styled.span`
  font-family: 'DM Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.text.muted};
  display: block;
  margin-bottom: 16px;
`;

const BookingTitle = styled.h3`
  font-family: 'Fraunces', 'Times New Roman', Georgia, serif;
  font-size: clamp(28px, 4vw, 40px);
  font-weight: 500;
  letter-spacing: -0.03em;
  line-height: 1.1;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 12px;
`;

const BookingDescription = styled.p`
  font-size: 15px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
`;

const EmbedFrame = styled.div`
  position: relative;
  width: 100%;
  height: 1100px;
  border-radius: 8px;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.bg};

  @media (max-width: 768px) {
    height: 1180px;
  }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
`;

const EmbedPlaceholder = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: ${({ theme }) => theme.colors.text.muted};
  font-family: 'DM Mono', monospace;
  font-size: 12px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  pointer-events: none;
  z-index: 1;
  background: ${({ theme }) => theme.colors.bg};

  span:last-child {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.accent};
    animation: ${pulse} 1.2s ease-in-out infinite;
  }
`;

const EmbedLayer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
`;

const FooterLink = styled.a`
  display: inline-block;
  margin-top: 20px;
  font-family: 'DM Mono', monospace;
  font-size: 12px;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.colors.text.muted};
  text-decoration: none;
  text-align: center;
  width: 100%;
  transition: color 120ms ease;

  &:hover {
    color: ${({ theme }) => theme.colors.accent};
  }
`;

const Contact = () => {
  const { themeObject, mode } = useTheme();
  const [ready, setReady] = useState(false);

  // Open TCP/TLS to Calendly origins as soon as Contact mounts so the actual
  // iframe load doesn't pay for DNS + handshake. Cheap, dropped on unmount.
  useEffect(() => {
    const links = CALENDLY_ORIGINS.flatMap((href) => {
      const preconnect = document.createElement('link');
      preconnect.rel = 'preconnect';
      preconnect.href = href;
      preconnect.crossOrigin = 'anonymous';
      const dns = document.createElement('link');
      dns.rel = 'dns-prefetch';
      dns.href = href;
      document.head.appendChild(preconnect);
      document.head.appendChild(dns);
      return [preconnect, dns];
    });
    return () => links.forEach((l) => l.remove());
  }, []);

  useCalendlyEventListener({
    onProfilePageViewed: () => setReady(true),
    onEventTypeViewed: () => setReady(true),
    onEventScheduled: () => trackContactClick('calendly_scheduled'),
  });

  // Safety net: if Calendly doesn't fire ready events (ad blockers, network
  // hiccups), hide the placeholder after 6s so users aren't stuck on it.
  useEffect(() => {
    if (ready) return;
    const t = window.setTimeout(() => setReady(true), 6000);
    return () => window.clearTimeout(t);
  }, [ready]);

  const stripHash = (hex) => hex.replace('#', '');
  const pageSettings = {
    backgroundColor: stripHash(themeObject.colors.bg),
    primaryColor: stripHash(themeObject.colors.accent),
    textColor: stripHash(themeObject.colors.text.primary),
    hideEventTypeDetails: false,
    hideLandingPageDetails: false,
    hideGdprBanner: true,
  };

  return (
    <ContactSection id="contact">
      <SectionTitle>Get in touch</SectionTitle>
      <ContentWrapper>
        <BookingCard
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          viewport={{ once: true }}
        >
          <BookingHeader>
            <BookingEye>Now booking · Q3 2026</BookingEye>
            <BookingTitle>Let's build something great</BookingTitle>
            <BookingDescription>
              Pick a time that works. We'll talk scope, timeline, and whether we're the right fit.
            </BookingDescription>
          </BookingHeader>

          <EmbedFrame>
            {!ready && (
              <EmbedPlaceholder>
                <span>Loading calendar</span>
                <span />
              </EmbedPlaceholder>
            )}
            <EmbedLayer>
              <InlineWidget
                key={mode}
                url={CALENDLY_URL}
                styles={{ height: '100%', width: '100%' }}
                pageSettings={pageSettings}
              />
            </EmbedLayer>
          </EmbedFrame>

          <FooterLink
            href={CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackContactClick('calendly_new_tab')}
          >
            Open in new tab ↗
          </FooterLink>
        </BookingCard>
      </ContentWrapper>
    </ContactSection>
  );
};

export default Contact;
